import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Camera, History as HistoryIcon, Navigation, Loader2, Target, TrendingDown, TrendingUp, LayoutGrid, X, Send, Eye, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { FUEL_TYPES } from '../../mock/mock';
import { api, photoURL } from '../../lib/api';
import { getCachedStations } from '../../lib/offline';
import { useAuth } from '../../context/AuthContext';
import { PhotoZoomSheet } from '../../components/PhotoZoomSheet';
import { HappyHourRequestDialog } from '../../components/HappyHourRequestDialog';

const StationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTM = user?.role === 'territory_manager';
  const [hhOpen, setHhOpen] = useState(false);
  const [station, setStation] = useState(null);
  const [entries, setEntries] = useState([]);
  const [competitors, setCompetitors] = useState(null);
  const [cmpRow, setCmpRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoView, setPhotoView] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [sendingTracking, setSendingTracking] = useState(false);
  const [confirmWin, setConfirmWin] = useState(null);
  const [lastTracking, setLastTracking] = useState(null);
  const [confirmPhoto, setConfirmPhoto] = useState(null);
  const [matrixPhoto, setMatrixPhoto] = useState(null);
  const [rsopRef, setRsopRef] = useState(null);

  useEffect(() => {
    if (matrixOpen && station?.is_own) {
      api.get(`/stations/${id}/rsop-reference`).then(r => setRsopRef(r.data)).catch(() => setRsopRef(null));
    }
  }, [matrixOpen, station?.is_own, id]);

  useEffect(() => {
    Promise.all([
      api.get('/stations').catch(async () => ({ data: await getCachedStations() })),
      api.get('/prices', { params: { station_id: id, limit: 10 } }).catch(() => ({ data: [] })),
      api.get('/my-competitors', { params: { station_id: id } }).catch(() => ({ data: [] })),
      api.get('/tracking-compliance').catch(() => ({ data: null })),
      api.get(`/stations/${id}/last-tracking-status`).catch(() => ({ data: null })),
      api.get(`/stations/${id}/last-confirm-photo`).catch(() => ({ data: null })),
    ]).then(([sr, pr, cr, tc, lt, cp]) => {
      setStation(sr.data.find(s => s.id === id));
      setEntries(pr.data);
      if (lt.data?.status) setLastTracking(lt.data);
      if (cp.data?.photo_url) setConfirmPhoto(cp.data);
      if (cr.data) {
        const row = (cr.data || []).find(x => x.station_id === id);
        setCompetitors(row ? row.competitors : []);
        setCmpRow(row || null);
      }
      if (tc.data?.stations) {
        setCompliance({ target: tc.data.target, ...(tc.data.stations.find(x => x.id === id) || {}) });
      }
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="animate-spin text-[#8a8579]"/></div>;
  if (!station) return <div className="p-6">Estación no encontrada</div>;

  const lastPrice = entries[0];
  // Precios propios consistentes con el tracking (capturas + oficial); fallback a oficiales
  const ownCmp = cmpRow?.own_prices && Object.keys(cmpRow.own_prices).length ? cmpRow.own_prices : null;
  const own = ownCmp || station.official_prices || {};
  const diffMin = typeof cmpRow?.diff_min === 'number' ? cmpRow.diff_min : 0.02;
  const ownVal = (fid, svc) => {
    const v = own[fid]?.[svc] ?? (svc === 'self' ? own[fid]?.base : null);
    return typeof v === 'number' && v > 0 ? v : null;
  };
  // Vista completa (precios, trackings y markers) para TODOS los roles móviles en estaciones propias
  const tmOwnView = station.is_own && !station.tm_marker_only;

  // Solicitud manual de cambio de precio (antes vivía en la pantalla Monitoreo)
  const submitTracking = async (force) => {
    setSendingTracking(true);
    try {
      const comps = cmpRow?.competitors || [];
      const ourPrices = station.official_prices || {};
      const diffs = {};
      const fuelIds = station.available_products?.length ? station.available_products : FUEL_TYPES.map(f => f.id);
      for (const k of fuelIds) {
        const p = ourPrices[k];
        if (!p) continue;
        for (const svc of ['self', 'full']) {
          const ov = p[svc];
          if (ov == null) continue;
          for (const c of comps) {
            const cv = c.prices?.[k]?.[svc];
            if (cv != null && Number(cv) !== 0) {
              const d = Number(cv) - Number(ov);
              if (Math.abs(d) > 0.001) {
                diffs[`${k}.${svc}`] = { our: Number(ov), competitor: Number(cv), competitor_id: c.id, delta: Number(d.toFixed(2)) };
                break;
              }
            }
          }
        }
      }
      const res = await api.post(`/stations/${station.id}/request-price-change`, {
        our_prices: ourPrices,
        competitor_snapshot: comps.map(c => ({ id: c.id, name: c.name, brand: c.brand, prices: c.prices })),
        diffs,
        force: !!force,
      });
      if (res.data?.merged) toast.success('Ya había un tracking pendiente de esta estación · se actualizó con los datos más recientes (no se duplicó)', { duration: 7000 });
      else toast.success('Tracking enviado · pendiente de aprobación');
      if (res.data?.incomplete && (res.data.missing_markers || []).length > 0) {
        toast.warning(`Tracking INCOMPLETO · sin captura reciente de: ${res.data.missing_markers.join(', ')}`, { duration: 9000 });
      }
      setConfirmWin(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === 'within_window') setConfirmWin(detail);
      else toast.error(typeof detail === 'string' ? detail : 'Error al enviar tracking');
    } finally { setSendingTracking(false); }
  };
  // Mismas reglas que las alertas: solo AUTO y |dif| > umbral
  const totalDiffs = (competitors || []).reduce((acc, c) => {
    let n = 0;
    Object.entries(c.prices || {}).forEach(([fid, svcs]) => {
      const mv = svcs?.self; const ov = ownVal(fid, 'self');
      if (mv > 0 && ov != null && Math.abs(mv - ov) > diffMin + 1e-9) n += 1;
    });
    return acc + n;
  }, 0);

  // Vista resumen: solo markers trackeados en la última ronda; el resto se lista como faltante
  const matrixComps = (competitors || []).filter(c => c.tracked_in_last_round !== false);
  const matrixMissing = (competitors || []).filter(c => c.tracked_in_last_round === false);

  return (
    <div className="px-5 py-5 fade-in">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-[12px] text-[#5a5a5a] mb-5"><ArrowLeft size={14}/> Volver</button>

      <p className="text-[10px] tracking-wider uppercase text-[#8a8579]">{station.brand}{station.bu ? ` · BU ${station.bu}` : ''}</p>
      <h1 className="font-serif text-[30px] leading-tight tracking-tight mt-1">{station.name}</h1>
      <div className="flex items-start gap-2 text-[13px] text-[#5a5a5a] mt-2"><MapPin size={14} className="mt-0.5 shrink-0"/><span>{station.address}</span></div>

      <div className="flex gap-2 mt-5">
        {!isTM && (!station.is_own || station.trackable_as_marker) && (
          <Button onClick={() => navigate(`/app/capture/${station.id}`)} className="press-soft flex-1 h-11 bg-[#0f1d6f] hover:bg-[#0a1450] text-[#f8f6f1] rounded-full text-[13px]" data-testid="btn-go-capture"><Camera size={14} className="mr-1.5"/> Capturar</Button>
        )}
        <Button onClick={() => navigate(`/app/map?focus=${station.id}`)} variant="outline" className={`press-soft h-11 px-4 border-[#1c1c1c]/30 rounded-full text-[13px] ${!isTM ? '' : 'flex-1'}`} data-testid="btn-view-markers"><Target size={14}/><span className="ml-1.5">Markers</span></Button>
      </div>

      {station.is_own && station.official_prices && Object.keys(station.official_prices).length > 0 && (
        <Button
          onClick={() => submitTracking(false)}
          disabled={sendingTracking}
          className="press-soft w-full h-11 mt-2 bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] rounded-full text-[13px]"
          data-testid="btn-send-tracking"
        >
          {sendingTracking ? <Loader2 size={14} className="animate-spin"/> : <><Send size={14} className="mr-1.5"/> Enviar tracking de precios</>}
        </Button>
      )}

      {isTM && station.is_own && (
        <>
          <Button
            onClick={() => setHhOpen(true)}
            variant="outline"
            className="press-soft w-full h-11 mt-2 border-[#0f1d6f]/40 text-[#0f1d6f] rounded-full text-[13px]"
            data-testid="btn-request-hh"
          >
            <Clock size={14} className="mr-1.5"/> Solicitar Happy Hour
          </Button>
          <HappyHourRequestDialog station={station} open={hhOpen} onOpenChange={setHhOpen}/>
        </>
      )}

      {confirmWin && (
        <div className="fixed inset-0 z-[1300] bg-black/50 flex items-center justify-center p-6" data-testid="tracking-window-confirm">
          <div className="bg-white rounded-2xl p-5 w-full max-w-[360px] space-y-3">
            <h3 className="font-serif text-[18px]">Cambio reciente</h3>
            <p className="text-[12.5px] text-[#5a5a5a]">{confirmWin.message || `Esta estación cambió precio hace poco (quedan ${confirmWin.hours_remaining} h de la ventana de ${confirmWin.window_hours} h). ¿Enviar de todos modos?`}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmWin(null)} className="flex-1 h-10 rounded-full text-[12.5px]" data-testid="confirm-window-cancel">Cancelar</Button>
              <Button onClick={() => submitTracking(true)} disabled={sendingTracking} className="flex-1 h-10 rounded-full bg-[#991b1b] hover:bg-[#7f1616] text-white text-[12.5px]" data-testid="confirm-window-force">Enviar de todos modos</Button>
            </div>
          </div>
        </div>
      )}

      {tmOwnView && (
        <div className="mt-7" data-testid="tm-station-view">
          <div className="flex items-end justify-between gap-2 mb-3">
            <h3 className="text-[12px] tracking-wider uppercase text-[#8a8579]">Precios de mi estación</h3>
            <div className="flex items-center gap-2 shrink-0">
              {cmpRow?.own_prices_as_of && (
                <span className="text-[10.5px] text-[#8a8579] font-mono" data-testid="own-prices-as-of">
                  {new Date(cmpRow.own_prices_as_of).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
              )}
              {compliance?.status && (
                <span
                  className={`text-[10.5px] font-mono px-2 py-0.5 rounded-full ${compliance.status === 'green' ? 'bg-[#dcfce7] text-[#166534]' : compliance.status === 'orange' ? 'bg-[#fef3c7] text-[#92400e]' : 'bg-[#fee2e2] text-[#991b1b]'}`}
                  title="Trackings completados hoy"
                  data-testid="own-compliance-badge"
                >
                  {compliance.rounds_today ?? 0}/{compliance.target ?? 3}
                </span>
              )}
            </div>
          </div>

          {/* Resolución del último tracking por Pricing */}
          {lastTracking?.status && (
            <div className="mb-3" data-testid="last-tracking-review">
              {lastTracking.status === 'reviewed' ? (
                <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-[#e0e7ff] text-[#3730a3] font-medium">
                  <Eye size={11}/> Visto por Pricing · {lastTracking.reviewed_by_name || ''}{lastTracking.reviewed_at ? ` · ${new Date(lastTracking.reviewed_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}` : ''} · sin cambio
                </span>
              ) : lastTracking.status === 'approved' ? (
                <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-[#dcfce7] text-[#166534] font-medium">
                  <Eye size={11}/> Tracking aprobado por Pricing{lastTracking.reviewed_at ? ` · ${new Date(lastTracking.reviewed_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
                </span>
              ) : lastTracking.status === 'rejected' ? (
                <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-[#f3f0e8] text-[#5a5a5a] font-medium">
                  <Eye size={11}/> Tracking rechazado por Pricing{lastTracking.reviewed_at ? ` · ${new Date(lastTracking.reviewed_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-[#fef3c7] text-[#92400e] font-medium">
                  <Eye size={11}/> Último tracking en revisión por Pricing
                </span>
              )}
            </div>
          )}

          <div className="bg-white border border-[#e8e5de] rounded-xl divide-y divide-[#e8e5de]">
            {FUEL_TYPES.filter(ft => ownVal(ft.id, 'self') != null || ownVal(ft.id, 'full') != null).map(ft => (
              <div key={ft.id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2.5"><span className="w-1.5 h-6 rounded-full" style={{ background: ft.color }}/><span className="text-[13.5px]">{ft.name}</span></div>
                <div className="text-right font-mono tabular-nums text-[13px]">
                  <span>{ownVal(ft.id, 'self') != null ? `Q${ownVal(ft.id, 'self').toFixed(2)}` : '—'}</span>
                  <span className="text-[#cfcabe] mx-1.5">·</span>
                  <span className="text-[#8a8579]">{ownVal(ft.id, 'full') != null ? `Q${ownVal(ft.id, 'full').toFixed(2)} full` : 'full —'}</span>
                </div>
              </div>
            ))}
            {Object.keys(own).length === 0 && <p className="px-4 py-4 text-[12px] text-[#8a8579]">Sin precios oficiales registrados.</p>}
          </div>

          <div className="flex items-center justify-between mt-7 mb-3">
            <h3 className="text-[12px] tracking-wider uppercase text-[#8a8579] flex items-center gap-1.5"><Target size={12}/> Markers · {(competitors || []).length}</h3>
            <div className="flex items-center gap-2">
              {(competitors || []).length > 0 && (
                <button
                  onClick={() => setMatrixOpen(true)}
                  className="press inline-flex items-center gap-1.5 text-[10.5px] px-2.5 py-1 rounded-full border border-[#1c1c1c]/25 bg-white text-[#1c1c1c]"
                  data-testid="btn-open-matrix"
                >
                  <LayoutGrid size={11}/> Vista resumen
                </button>
              )}
              {totalDiffs > 0 && (
                <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-[#fee2e2] text-[#991b1b]" data-testid="tm-diff-count">{totalDiffs} diferencia(s)</span>
              )}
            </div>
          </div>
          {(competitors || []).length === 0 && (
            <p className="text-[12.5px] text-[#8a8579] bg-white border border-[#e8e5de] rounded-xl p-4">Esta estación no tiene markers configurados.</p>
          )}

          {matrixOpen && (
            <div className="fixed inset-0 z-[1300] bg-black/50 flex items-end" onClick={() => setMatrixOpen(false)} data-testid="matrix-sheet">
              <div className="bg-[#f8f6f1] w-full max-h-[85vh] rounded-t-2xl overflow-hidden flex flex-col" style={{ maxHeight: '85dvh', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#e8e5de]">
                  <div className="flex items-center gap-2 min-w-0">
                    <h3 className="font-serif text-[17px] truncate">Resumen · {station.name}</h3>
                    {compliance?.last_incomplete && (
                      <span className="text-[9.5px] px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e] uppercase tracking-wide shrink-0" data-testid="matrix-incomplete-chip">Incompleto</span>
                    )}
                  </div>
                  <button onClick={() => setMatrixOpen(false)} className="w-8 h-8 rounded-full hover:bg-[#e8e5de] flex items-center justify-center shrink-0" data-testid="matrix-close"><X size={16}/></button>
                </div>
                {station.is_own && rsopRef?.rsop && Object.keys(rsopRef.rsop).length > 0 && (
                  <p className="text-[10.5px] mx-4 mt-2 px-3 py-1.5 rounded-lg bg-[#dcfce7] text-[#166534] border border-[#bbf7d0]" data-testid="matrix-rsop-line">
                    <strong>RSOP referencial:</strong>{' '}
                    {FUEL_TYPES.filter(ft => rsopRef.rsop[ft.id]?.self != null).map(ft => `${ft.name} Q${rsopRef.rsop[ft.id].self.toFixed(2)}`).join(' · ')}
                  </p>
                )}
                {compliance?.last_tracking_at && (
                  <p className="text-[10.5px] text-[#8a8579] px-4 pt-2">
                    Último tracking: {new Date(compliance.last_tracking_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}{compliance.last_incomplete ? ' · incompleto' : ''}
                  </p>
                )}
                {matrixMissing.length > 0 && (
                  <p className="text-[10.5px] mx-4 mt-2 px-3 py-1.5 rounded-lg bg-[#fef3c7] text-[#92400e] border border-[#fde68a]" data-testid="matrix-missing-markers">
                    <strong>Markers sin tracking en la última ronda:</strong>{' '}
                    {matrixMissing.map(c => c.name).join(' · ')}
                  </p>
                )}
                {confirmPhoto && (
                  <div className="flex flex-wrap gap-2 px-4 pt-2">
                    <button
                      onClick={() => setMatrixPhoto({ src: photoURL(confirmPhoto.photo_url), title: `${station.name} · Última confirmación (monolito)${confirmPhoto.confirmed_at ? ` · ${new Date(confirmPhoto.confirmed_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}` : ''}` })}
                      className="press inline-flex items-center gap-1.5 text-[10.5px] px-2.5 py-1 rounded-full bg-[#fef3c7] text-[#92400e]"
                      data-testid="matrix-confirm-photo-btn"
                    >
                      <Camera size={11}/> Última confirmación
                    </button>
                  </div>
                )}
                <div className="overflow-auto p-3">
                  {(() => {
                    const visFuels = FUEL_TYPES.filter(ft => ownVal(ft.id, 'self') != null || matrixComps.some(c => c.prices?.[ft.id]?.self > 0));
                    return (<>
                  <table className="text-[11px] border-separate border-spacing-0 min-w-full">
                    <thead>
                      <tr>
                        <th className="sticky left-0 bg-[#f8f6f1] text-left py-1.5 pr-2 text-[9.5px] uppercase tracking-wider text-[#8a8579] font-normal">Estación</th>
                        {visFuels.map(ft => (
                          <th key={ft.id} className="py-1.5 px-1.5 text-[9.5px] uppercase tracking-wider text-[#8a8579] font-normal text-center border-l border-[#e8e5de]">{ft.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[{ own: true, id: station.id, name: station.name, brand: station.brand }, ...matrixComps].map(row => {
                        return (
                          <tr key={row.id} className={row.own ? 'bg-[#fdf6e3]' : 'bg-white'}>
                            <td className={`sticky left-0 py-2 pr-2 pl-1 border-t border-[#e8e5de] ${row.own ? 'bg-[#fdf6e3]' : 'bg-white'}`}>
                              <div className="flex items-center gap-1.5">
                                <div className="min-w-0">
                                  <p className="text-[11px] font-medium leading-tight max-w-[100px] truncate">{row.name}</p>
                                  <p className="text-[8.5px] text-[#8a8579]">{row.brand}{row.own ? ' · propia' : ''}</p>
                                </div>
                                {!row.own && (row.photos?.self || row.photos?.full) && (
                                  <button
                                    onClick={() => {
                                      const ph = row.photos.self || row.photos.full;
                                      setMatrixPhoto({ src: photoURL(ph), title: `${row.name} · Monolito último tracking${row.last_approved_at ? ` · ${new Date(row.last_approved_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}` : ''}` });
                                    }}
                                    className="press shrink-0 w-6 h-6 rounded-full bg-[#e7eeff] text-[#0f1d6f] flex items-center justify-center"
                                    data-testid={`matrix-marker-photo-${row.id}`}
                                    aria-label={`Foto monolito ${row.name}`}
                                  >
                                    <Camera size={11}/>
                                  </button>
                                )}
                              </div>
                            </td>
                            {visFuels.map(ft => {
                              const v = row.own ? ownVal(ft.id, 'self') : (row.prices?.[ft.id]?.self > 0 ? row.prices[ft.id].self : null);
                              let cell;
                              if (v == null) cell = <span className="text-[#cfcabe]">—</span>;
                              else if (row.own) cell = <span className="font-mono">Q{v.toFixed(2)}</span>;
                              else {
                                const ov = ownVal(ft.id, 'self');
                                const cls = ov == null || Math.abs(v - ov) <= diffMin + 1e-9 ? '' : (v < ov ? 'bg-[#fee2e2] text-[#991b1b]' : 'bg-[#dcfce7] text-[#166534]');
                                cell = <span className={`font-mono px-1 py-0.5 rounded ${cls}`}>Q{v.toFixed(2)}</span>;
                              }
                              return <td key={ft.id} className="py-2 px-1.5 text-center border-t border-l border-[#e8e5de] whitespace-nowrap">{cell}</td>;
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p className="text-[9.5px] text-[#8a8579] mt-2 pb-2">Precios Autoservicio · ■ rojo: menor que nuestro precio · ■ verde: mayor · — sin dato.</p>
                    </>);
                  })()}
                </div>
              </div>
              {matrixPhoto && (
                <div className="fixed inset-0 z-[1400] bg-black/85 flex flex-col items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setMatrixPhoto(null); }} data-testid="matrix-photo-view">
                  <p className="text-white text-[12px] mb-2 text-center px-2">{matrixPhoto.title}</p>
                  <img src={matrixPhoto.src} alt={matrixPhoto.title} className="max-h-[78vh] max-w-full rounded-lg object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling && (e.currentTarget.nextSibling.style.display = 'block'); }}/>
                  <p className="text-white/80 text-[12px] hidden bg-white/10 rounded-lg px-4 py-3" data-testid="matrix-photo-missing">Foto no disponible (fue descargada o eliminada del historial)</p>
                  <button className="mt-3 text-white/90 text-[12.5px] underline underline-offset-4" data-testid="matrix-photo-close">Cerrar</button>
                </div>
              )}
            </div>
          )}
          <div className="space-y-3">
            {(competitors || []).map(c => {
              const rows = [];
              FUEL_TYPES.forEach(ft => {
                const mv = c.prices?.[ft.id]?.self;
                const ov = ownVal(ft.id, 'self');
                if (mv > 0 && ov != null) rows.push({ ft, svc: 'self', mv, ov, diff: +(mv - ov).toFixed(2) });
              });
              const diffRows = rows.filter(r => Math.abs(r.diff) > diffMin + 1e-9);
              return (
                <div key={c.id} className="bg-white border border-[#e8e5de] rounded-xl p-4" data-testid={`tm-marker-${c.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium truncate">{c.name}</p>
                      <p className="text-[10.5px] text-[#8a8579]">{c.brand}{c.bu ? ` · ${c.bu}` : ''}{c.last_approved_at ? ` · ${new Date(c.last_approved_at).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'short' })}` : ''}</p>
                    </div>
                    {diffRows.length > 0
                      ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#fee2e2] text-[#991b1b] shrink-0">{diffRows.length} dif.</span>
                      : <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#dcfce7] text-[#166534] shrink-0">Sin diferencias</span>}
                  </div>
                  {diffRows.length > 0 && (
                    <div className="mt-2.5 space-y-1.5">
                      {diffRows.map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-[12px]">
                          <span className="flex items-center gap-1.5 text-[#5a5a5a]">
                            <span className="w-1 h-4 rounded-full" style={{ background: r.ft.color }}/>
                            {r.ft.name} · {r.svc === 'self' ? 'Auto' : 'Completo'}
                          </span>
                          <span className="font-mono tabular-nums flex items-center gap-1.5">
                            <span className="text-[#8a8579]">Q{r.mv.toFixed(2)} vs Q{r.ov.toFixed(2)}</span>
                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium ${r.diff < 0 ? 'bg-[#fee2e2] text-[#991b1b]' : 'bg-[#dcfce7] text-[#166534]'}`}>
                              {r.diff < 0 ? <TrendingDown size={10}/> : <TrendingUp size={10}/>}
                              {r.diff > 0 ? '+' : ''}{r.diff.toFixed(2)}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(c.photos?.self || c.photos?.full) && (
                    <div className="flex gap-2 mt-2.5">
                      {c.photos?.self && (
                        <button
                          onClick={() => setPhotoView({ src: photoURL(c.photos.self), title: `${c.name} · Auto` })}
                          className="press inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-[#e8e5de] bg-[#f8f6f1] text-[#0f1d6f]"
                          data-testid={`tm-marker-photo-self-${c.id}`}
                        >
                          <Camera size={11}/> Foto Auto
                        </button>
                      )}
                      {c.photos?.full && (
                        <button
                          onClick={() => setPhotoView({ src: photoURL(c.photos.full), title: `${c.name} · Completo` })}
                          className="press inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border border-[#e8e5de] bg-[#f8f6f1] text-[#0f1d6f]"
                          data-testid={`tm-marker-photo-full-${c.id}`}
                        >
                          <Camera size={11}/> Foto Full
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {lastPrice && !tmOwnView && (
        <div className="mt-7">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[12px] tracking-wider uppercase text-[#8a8579]">Últimos precios</h3>
            <span className="text-[11px] text-[#8a8579]">{new Date(lastPrice.captured_at).toLocaleDateString('es-GT')}</span>
          </div>
          <div className="bg-white border border-[#e8e5de] rounded-xl divide-y divide-[#e8e5de]">
            {FUEL_TYPES.map(ft => (
              <div key={ft.id} className="flex items-center justify-between px-4 py-3.5">
                <div className="flex items-center gap-2.5"><span className="w-1.5 h-6 rounded-full" style={{ background: ft.color }}/><span className="text-[13.5px]">{ft.name}</span></div>
                <span className="font-mono text-[14px] tabular-nums">{lastPrice.prices[ft.id] != null ? `Q ${Number(lastPrice.prices[ft.id]).toFixed(2)}` : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {entries.length > 0 && (
        <div className="mt-7">
          <div className="flex items-center gap-2 mb-3"><HistoryIcon size={13} className="text-[#8a8579]"/><h3 className="text-[12px] tracking-wider uppercase text-[#8a8579]">Histórico</h3></div>
          <div className="space-y-2">
            {entries.map(e => (
              <div key={e.id} className="bg-white border border-[#e8e5de] rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-[12px] font-medium">{e.captured_by_name}</p>
                  <p className="text-[11px] text-[#8a8579]">{new Date(e.captured_at).toLocaleString('es-GT')}</p>
                </div>
                <div className="flex items-center gap-2">
                  {e.photo_url && (
                    <button
                      onClick={() => setPhotoView({ src: photoURL(e.photo_url), title: `${station.name} · ${new Date(e.captured_at).toLocaleDateString('es-GT')}` })}
                      className="press text-[#0f1d6f]"
                      data-testid={`entry-photo-${e.id}`}
                      aria-label="Ver foto"
                    >
                      <Camera size={15}/>
                    </button>
                  )}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${e.status==='approved'?'bg-[#1c1c1c] text-[#f8f6f1]':e.status==='flagged'?'bg-[#fee2e2] text-[#991b1b]':e.status==='rejected'?'bg-[#e8e5de] text-[#5a5a5a]':'bg-[#fef3c7] text-[#92400e]'}`}>
                    {e.status==='approved'?'Aprobada':e.status==='flagged'?'Marcada':e.status==='rejected'?'Rechazada':'Pendiente'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <PhotoZoomSheet open={!!photoView} onOpenChange={(o) => { if (!o) setPhotoView(null); }} src={photoView?.src} title={photoView?.title}/>
    </div>
  );
};

export default StationDetail;
